// Custom hook for translation with i18n fallback and Bhashini API
// Use this hook when you need to translate dynamic text that may not be in i18n

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import translationService from '../services/translationService';

/**
 * Hook for translating text with i18n first, then API fallback for dynamic text
 *
 * @param {string} keyOrText - i18n key or plain text to translate
 * @param {object} options - Translation options
 * @returns {string} - Translated text (may update async for dynamic text)
 *
 * @example
 * // For static text (i18n key)
 * const title = useTranslationWithFallback('nav.home');
 *
 * @example
 * // For dynamic text (will use API fallback if not in i18n)
 * const dynamicText = useTranslationWithFallback(userComment, { useFallback: true });
 */
function useTranslationWithFallback(keyOrText, options = {}) {
  const { i18n } = useTranslation();
  
  // Guard 1: Ensure we aren't starting with an object (like a props object)
  const initialValue = typeof keyOrText === 'object' ? '' : (keyOrText || '');
  const [translatedText, setTranslatedText] = useState(initialValue);

  const {
    useFallback = true,
    sourceLang = 'en',
    // Guard 2: Ensure defaultValue is a string
    defaultValue = typeof options.defaultValue === 'object' ? '' : (options.defaultValue || initialValue),
  } = options;

  // Memoize the translation key/text to avoid unnecessary recalculations
  const memoizedKeyOrText = useMemo(() => keyOrText, [keyOrText]);
  const currentLang = options.targetLang || i18n.language;

 useEffect(() => {
    // Guard 3: If keyOrText is accidentally an object, don't try to translate it
    if (typeof memoizedKeyOrText === 'object') {
      console.warn("useTranslationWithFallback received an object instead of a string:", memoizedKeyOrText);
      setTranslatedText('');
      return;
    }

    const i18nTranslation = i18n.t(memoizedKeyOrText, {
      returnObjects: false,
      defaultValue: null,
    });

    if (i18nTranslation && i18nTranslation !== memoizedKeyOrText) {
      setTranslatedText(i18nTranslation);
      return;
    }

    if (useFallback && currentLang !== sourceLang) {
      const cachedTranslation = translationService.getCachedTranslation(
        memoizedKeyOrText,
        currentLang
      );

      if (cachedTranslation) {
        setTranslatedText(cachedTranslation);
        return;
      }

      setTranslatedText(defaultValue);

   translationService
        .translateWithAPI(memoizedKeyOrText, sourceLang, currentLang)
        .then((translated) => {
          // FORCE the result to be a string. If it's an object, extract the text.
          let finalResult = "";
          
          if (typeof translated === 'string') {
            finalResult = translated;
          } else if (translated && typeof translated === 'object') {
            finalResult = translated.translatedText || translated.text || JSON.stringify(translated);
          } else {
            finalResult = defaultValue;
          }

          setTranslatedText(finalResult);
        })
        .catch(() => {
          setTranslatedText(defaultValue);
        });
    } else {
      setTranslatedText(defaultValue);
    }
  }, [memoizedKeyOrText, currentLang, i18n, useFallback, sourceLang, defaultValue]);


  return typeof translatedText === 'object' ? JSON.stringify(translatedText) : translatedText;
}

/**
 * Hook for translating multiple texts efficiently
 * Useful for translating arrays of dynamic text
 */
function useMultipleTranslations(texts, options = {}) {
  const { i18n } = useTranslation();
  const [translatedTexts, setTranslatedTexts] = useState({});
  const currentLang = options.targetLang || i18n.language;

  useEffect(() => {
    if (!Array.isArray(texts) || texts.length === 0) {
      setTranslatedTexts({});
      return;
    }

    const translations = {};
    const textsToTranslate = [];

    // First pass: check i18n and cache
    texts.forEach((text, index) => {
      const i18nTranslation = i18n.t(text, {
        returnObjects: false,
        defaultValue: null,
      });

      if (i18nTranslation && i18nTranslation !== text) {
        translations[index] = i18nTranslation;
      } else {
        // Check cache
        const cachedTranslation = translationService.getCachedTranslation(text, currentLang);
        if (cachedTranslation) {
          translations[index] = cachedTranslation;
        } else {
          textsToTranslate.push({ index, text });
        }
      }
    });

    // Second pass: translate remaining texts via API
    if (textsToTranslate.length > 0) {
      Promise.all(
        textsToTranslate.map(({ index, text }) =>
          translationService
            .translateWithAPI(text, options.sourceLang || 'en', currentLang)
            .then((translated) => ({ index, translated }))
            .catch(() => ({ index, translated: text }))
        )
      ).then((results) => {
        results.forEach(({ index, translated }) => {
          translations[index] = translated;
        });
        setTranslatedTexts(translations);
      });
    } else {
      setTranslatedTexts(translations);
    }
  }, [texts, currentLang, i18n, options.sourceLang]);

  return (index) => translatedTexts[index] || texts[index] || '';
}

export { useTranslationWithFallback, useMultipleTranslations };
export default useTranslationWithFallback;
